import React, { useCallback, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { 
  useGetOrganisationEventsQuery, 
  useCreateEventMutation,
  useDeleteEventMutation
} from "../services/nestjs-base-api";
import { 
  Breadcrumb, 
  Button, 
  Card, 
  Col,
  DatePicker,
  Dropdown,
  Empty, 
  Form, 
  Input, 
  Layout,
  Modal,
  Row, 
  Space, 
  Spin,
  Typography, 
  message, 
  theme,
  Popover,
  QRCode
} from "antd";
import { 
  PlusOutlined, 
  CalendarOutlined, 
  EnvironmentOutlined, 
  LinkOutlined,
  QrcodeOutlined,
  DeleteOutlined,
  MoreOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { UserRoleEnum } from "../enums/UserRoleEnum";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useCopyToClipboard } from "react-use";
import { UpgradeModal } from "../components/UpgradeModal";
import { useMaxEvents } from "../hooks/useSystemSetting";
import styled from "styled-components";

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;
const { TextArea } = Input;
const { Meta } = Card;

interface IEventFormValues {
  name: string;
  description?: string;
  date: dayjs.Dayjs;
  location?: string;
}

const StyledContent = styled(Content)`
  margin: 0 24px;
  
  @media (max-width: 768px) {
    margin: 0 16px;
  }
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin: 24px 0 16px 0;
  
  .ant-breadcrumb-link {
    color: #666;
    
    &:hover {
      color: #4169e1;
    }
  }
`;

const ContentWrapper = styled.div`
  padding: 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const StyledTitle = styled(Title)`
  margin: 0 !important;
  color: #1a2847;
`;

const StyledButton = styled(Button)`
  &.ant-btn-primary {
    background: linear-gradient(135deg, #4169e1 0%, #1890ff 100%);
    border: none;
    height: 40px;
    padding: 0 24px;
    font-size: 15px;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(65, 105, 225, 0.3);
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(65, 105, 225, 0.4);
    }
  }
`;

const EventCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: #e6f7ff;
  }
  
  .ant-card-cover {
    background: linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%);
    height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .anticon {
      font-size: 48px;
      color: #4169e1;
    }
  }
  
  .ant-card-actions {
    border-top: 1px solid #f0f0f0;
    
    > li {
      margin: 8px 0;
    }
  }
  
  .ant-btn-text {
    color: #666;
    font-size: 13px;
    height: auto;
    padding: 4px 8px;
    
    &:hover {
      color: #4169e1;
      background: rgba(65, 105, 225, 0.05);
    }
  }
`;

const EmptyStateWrapper = styled.div`
  padding: 80px 0;
  text-align: center;
`;

const StyledModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: 1px solid #f0f0f0;
  }
  
  .ant-modal-title {
    color: #1a2847;
    font-size: 18px;
    font-weight: 600;
  }
  
  .ant-form-item-label > label {
    color: #666;
    font-weight: 500;
  }
  
  .ant-input,
  .ant-input-textarea,
  .ant-picker {
    border-radius: 8px;
    
    &:hover,
    &:focus {
      border-color: #4169e1;
    }
  }
`;

const EventsPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm<IEventFormValues>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<{id: number, hash: string, name: string} | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [, copyToClipboard] = useCopyToClipboard();
  
  // Get max events limit from system settings
  const maxEvents = useMaxEvents("5");
  
  // Check if user is org admin
  const isOrgAdmin = user?.roles?.includes(UserRoleEnum.OrganisationAdmin) || 
                    user?.roles?.includes(UserRoleEnum.SuperAdmin);

  // Get organization events
  const { 
    data: events, 
    error: eventsError, 
    isLoading: isLoadingEvents,
    refetch: refetchEvents
  } = useGetOrganisationEventsQuery({ 
    jwtToken: user.jwtToken, 
    organisationId: user.organisation.id 
  });

  // Create event mutation
  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();

  // Delete event mutation
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();

  // Handle create event submit
  const handleCreateEvent = useCallback(async (values: IEventFormValues) => {
    try {
      await createEvent({
        jwtToken: user.jwtToken,
        payload: {
          name: values.name,
          description: values.description,
          date: values.date.toISOString(),
          location: values.location,
          organisationId: user.organisation.id
        }
      }).unwrap();
      
      messageApi.success(intl.formatMessage({ id: 'events.createSuccess' }) || 'Event created successfully');
      setIsModalOpen(false);
      form.resetFields();
      refetchEvents();
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        logout();
      } else {
        messageApi.error(intl.formatMessage({ id: 'events.createError' }) || 'Error creating event');
      }
    }
  }, [createEvent, form, intl, logout, messageApi, refetchEvents, user.jwtToken, user.organisation.id]);

  // Check if user can create more events
  const canCreateMoreEvents = useCallback(() => {
    if (!events) return true; // If events data isn't loaded yet, allow creation
    
    const maxEventsCount = parseInt(maxEvents, 10) || 1;
    return events.length < maxEventsCount;
  }, [events, maxEvents]);

  // Show modal for creating event or show upgrade modal
  const showModal = () => {
    if (canCreateMoreEvents()) {
      form.resetFields();
      setIsModalOpen(true);
    } else {
      setIsUpgradeModalOpen(true);
    }
  };

  // Handle cancel modal
  const handleCancel = () => {
    setIsModalOpen(false);
  };
  
  // Handle close upgrade modal
  const handleCloseUpgradeModal = () => {
    setIsUpgradeModalOpen(false);
  };

  // Show delete confirmation modal
  const showDeleteConfirmation = (event: { id: number, hash: string, name: string }, e?: React.MouseEvent | React.KeyboardEvent | MouseEvent | KeyboardEvent) => {
    if (e && 'stopPropagation' in e) {
      e.stopPropagation(); // Prevent navigation to event details
    }
    setEventToDelete(event);
    setIsDeleteModalOpen(true);
  };

  // Handle delete event
  const handleDeleteEvent = useCallback(async () => {
    if (!eventToDelete) return;
    
    try {
      await deleteEvent({
        jwtToken: user.jwtToken,
        hash: eventToDelete.hash
      }).unwrap();
      
      messageApi.success(intl.formatMessage({ id: 'events.delete.success' }) || 'Event deleted successfully');
      setIsDeleteModalOpen(false);
      setEventToDelete(null);
      refetchEvents();
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        logout();
      } else {
        messageApi.error(intl.formatMessage({ id: 'events.delete.error' }) || 'Error deleting event');
      }
    }
  }, [deleteEvent, eventToDelete, intl, logout, messageApi, refetchEvents, user.jwtToken]);

  // Handle cancel delete
  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setEventToDelete(null);
  };

  // Copy event URL to clipboard
  const copyEventUrl = useCallback((hash: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const baseUrl = window.location.origin;
    const eventUrl = `${baseUrl}/event/${hash}`;
    copyToClipboard(eventUrl);
    messageApi.success(intl.formatMessage({ id: 'events.urlCopied' }) || 'Event URL copied to clipboard');
  }, [copyToClipboard, intl, messageApi]);

  // Navigate to event details
  const navigateToEventDetails = useCallback((eventId: number) => {
    navigate(`/dashboard/events/${eventId}`);
  }, [navigate]);

  // Check for errors
  React.useEffect(() => {
    if (eventsError) {
      if ('status' in eventsError && eventsError.status === 401) {
        logout();
      }
    }
  }, [eventsError, logout]);

  return (
    <StyledContent>
      {contextHolder}
      <StyledBreadcrumb>
        <Breadcrumb.Item>{user.organisation ? user.organisation.name : intl.formatMessage({ id: 'common.platformName' })}</Breadcrumb.Item>
        <Breadcrumb.Item><FormattedMessage id={'section.eventsName'} /></Breadcrumb.Item>
      </StyledBreadcrumb>
      <ContentWrapper>
        <PageHeader>
          <StyledTitle level={2}><FormattedMessage id={'section.eventsName'} /></StyledTitle>
          
          {isOrgAdmin && (
            <StyledButton 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={showModal}
            >
              {intl.formatMessage({ id: 'events.create' }) || 'Create Event'}
            </StyledButton>
          )}
        </PageHeader>

        {isLoadingEvents ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
            <Paragraph style={{ marginTop: 16, color: '#666' }}>
              {intl.formatMessage({ id: 'events.loading' }) || 'Loading events...'}
            </Paragraph>
          </div>
        ) : events && events.length > 0 ? (
          <Row gutter={[24, 24]}>
            {events.map(event => (
              <Col xs={24} sm={12} md={8} lg={6} key={event.id}>
                <EventCard
                  hoverable
                  cover={
                    <div
                        style={{
                          height: 140,
                          background: '#f0f5ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                    >
                      <CalendarOutlined />
                    </div>
                  }
                  actions={[
                    <Popover 
                      key="qrcode"
                      content={
                        <div style={{ textAlign: 'center' }}>
                          <QRCode
                            value={`${window.location.origin}/event/${event.hash}`}
                            size={200}
                            bordered={false}
                            style={{ margin: '10px 0' }}
                            icon="/favicon.ico"
                          />
                          <Typography.Paragraph style={{ marginBottom: 8 }}>
                            {event.name}
                          </Typography.Paragraph>
                          <Button 
                            type="primary" 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              const canvas = document.querySelector('.ant-qrcode canvas') as HTMLCanvasElement;
                              if (canvas) {
                                const url = canvas.toDataURL('image/png');
                                const a = document.createElement('a');
                                a.download = `${event.name} QR Code.png`;
                                a.href = url;
                                a.click();
                              }
                            }}
                          >
                            {intl.formatMessage({ id: 'events.qrcode.download' }) || 'Download QR Code'}
                          </Button>
                        </div>
                      }
                      trigger="click"
                      title={intl.formatMessage({ id: 'events.qrcode' }) || 'Event QR Code'}
                    >
                      <Button 
                        key="qrcode"
                        type="text" 
                        icon={<QrcodeOutlined />} 
                        onClick={(e) => e.stopPropagation()}
                      >
                        {intl.formatMessage({ id: 'events.table.qrcode' }) || 'QR Code'}
                      </Button>
                    </Popover>,
                    <Button 
                      key="link" 
                      type="text" 
                      icon={<LinkOutlined />} 
                      onClick={(e) => copyEventUrl(event.hash, e)}
                    >
                      {intl.formatMessage({ id: 'events.table.copyLink' }) || 'Copy Link'}
                    </Button>,
                    isOrgAdmin && (
                      <Dropdown 
                        key="more"
                        menu={{ 
                          items: [
                            {
                              key: 'delete',
                              icon: <DeleteOutlined />,
                              label: intl.formatMessage({ id: 'events.delete' }) || 'Delete Event',
                              danger: true,
                              onClick: (info) => {
                                info.domEvent.stopPropagation();
                                showDeleteConfirmation(event);
                              }
                            }
                          ],
                          onClick: (info) => {
                            info.domEvent.stopPropagation();
                          }
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                        arrow={{ pointAtCenter: true }}
                      >
                        <Button 
                          type="text" 
                          icon={<MoreOutlined />} 
                          onClick={(e) => e.stopPropagation()}
                        >
                          {intl.formatMessage({ id: 'events.menu.more' }) || 'More'}
                        </Button>
                      </Dropdown>
                    )
                  ]}
                  onClick={() => navigateToEventDetails(event.id)}
                >
                  <Meta
                    title={<span style={{ color: '#1a2847', fontSize: '16px', fontWeight: 600 }}>{event.name}</span>}
                    description={
                      <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 8 }}>
                        <div>
                          <CalendarOutlined style={{ marginRight: 8, color: '#4169e1' }} />
                          <Text style={{ color: '#666' }}>{new Date(event.date).toLocaleDateString()}</Text>
                        </div>
                        
                        {event.location && (
                          <div>
                            <EnvironmentOutlined style={{ marginRight: 8, color: '#4169e1' }} />
                            <Text style={{ color: '#666' }}>{event.location}</Text>
                          </div>
                        )}
                        
                        {event.description && (
                          <Paragraph 
                            ellipsis={{ rows: 2 }} 
                            style={{ marginTop: 8, marginBottom: 0, color: '#999' }}
                          >
                            {event.description}
                          </Paragraph>
                        )}
                      </Space>
                    }
                  />
                </EventCard>
              </Col>
            ))}
          </Row>
        ) : (
          <EmptyStateWrapper>
            <Empty 
              description={intl.formatMessage({ id: 'events.empty' }) || 'No events found'} 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </EmptyStateWrapper>
        )}
      </ContentWrapper>

      {/* Create Event Modal */}
      <StyledModal
        title={intl.formatMessage({ id: 'events.createTitle' }) || 'Create New Event'}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateEvent}
        >
          <Form.Item
            name="name"
            label={intl.formatMessage({ id: 'events.form.name' }) || 'Event Name'}
            rules={[{ required: true, message: intl.formatMessage({ id: 'events.form.nameRequired' }) || 'Please enter event name' }]}
          >
            <Input placeholder={intl.formatMessage({ id: 'events.form.namePlaceholder' }) || 'Enter event name'} />
          </Form.Item>

          <Form.Item
            name="description"
            label={intl.formatMessage({ id: 'events.form.description' }) || 'Description'}
          >
            <TextArea 
              rows={4} 
              placeholder={intl.formatMessage({ id: 'events.form.descriptionPlaceholder' }) || 'Enter event description'} 
            />
          </Form.Item>

          <Form.Item
            name="date"
            label={intl.formatMessage({ id: 'events.form.date' }) || 'Event Date'}
            rules={[{ required: true, message: intl.formatMessage({ id: 'events.form.dateRequired' }) || 'Please select a date' }]}
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format="YYYY-MM-DD"
              placeholder={intl.formatMessage({ id: 'events.form.datePlaceholder' }) || 'Select event date'}
            />
          </Form.Item>

          <Form.Item
            name="location"
            label={intl.formatMessage({ id: 'events.form.location' }) || 'Location'}
          >
            <Input 
              placeholder={intl.formatMessage({ id: 'events.form.locationPlaceholder' }) || 'Enter event location'}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                {intl.formatMessage({ id: 'common.cancel' }) || 'Cancel'}
              </Button>
              <StyledButton type="primary" htmlType="submit" loading={isCreating}>
                {intl.formatMessage({ id: 'common.create' }) || 'Create'}
              </StyledButton>
            </Space>
          </Form.Item>
        </Form>
      </StyledModal>

      {/* Upgrade Modal */}
      <Modal
        open={isUpgradeModalOpen}
        onCancel={handleCloseUpgradeModal}
        footer={null}
        closable={false}
      >
        <UpgradeModal 
          onClose={handleCloseUpgradeModal} 
          featureName={intl.formatMessage({ id: 'features.maxEvents.name' }) || 'Maximum Events'} 
          unlockLevel={intl.formatMessage(
            { id: 'organisation.plan.professional' },
            { plan: 'Professional' }
          ) || 'Professional Plan'}
        />
      </Modal>

      {/* Delete Event Confirmation Modal */}
      <StyledModal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
            {intl.formatMessage({ id: 'events.delete.confirm.title' }) || 'Delete Event'}
          </span>
        }
        open={isDeleteModalOpen}
        onCancel={handleCancelDelete}
        footer={[
          <Button key="cancel" onClick={handleCancelDelete}>
            {intl.formatMessage({ id: 'common.cancel' }) || 'Cancel'}
          </Button>,
          <Button 
            key="delete" 
            type="primary" 
            danger 
            loading={isDeleting}
            onClick={handleDeleteEvent}
          >
            {intl.formatMessage({ id: 'events.delete' }) || 'Delete Event'}
          </Button>
        ]}
      >
        <p>{intl.formatMessage({ id: 'events.delete.confirm.content' }) || 'This will permanently delete the event, all its galleries, and all photos. This action cannot be undone.'}</p>
        {eventToDelete && (
          <p style={{ fontWeight: 'bold' }}>
            "{eventToDelete.name}"
          </p>
        )}
      </StyledModal>
    </StyledContent>
  );
};

export default EventsPage;